import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { getConfig } from '@/config';
import {
  CompetitorV2ApiError,
  CompetitorV2Client,
  type DpMatch,
  type DpMatchCandidate,
  type DpMatchStatus,
  type DpPriceReviewItem,
  type DpPriceStatus,
  type DpReviewSummary,
} from '@/infrastructure/CompetitorV2Client';
import { useStaffSession } from '@/providers/useStaffSession';
import { DpStaffPriceReview } from './DpStaffPriceReview';
import {
  DP_PRICE_ERRORS,
  SOURCE_HOSTS,
  dpCurrentPriceText,
  dpProposedPrice,
  dpQueueContextText,
  holdReasonLabel,
  safeProductUrl,
} from './DpPriceReviewText';
import { QuantityFields, dpQuantityDraft, dpQuantityInput } from './DpQuantityFields';

const SOURCES = {
  stylekorean: 'StyleKorean',
  yesstyle: 'YesStyle',
  stylevana: 'Stylevana',
  oliveyoung_global: 'Olive Young Global',
  amazon: 'Amazon',
  oliveyoung_us: 'Olive Young US',
};
// The competitor codes the temporary single-match path accepts.
const SOURCE_CODES = new Map<string, keyof typeof SOURCES>([
  ['ys', 'yesstyle'],
  ['sk', 'stylekorean'],
  ['sv', 'stylevana'],
  ['oyg', 'oliveyoung_global'],
  ['oyu', 'oliveyoung_us'],
  ['amz', 'amazon'],
]);
const MATCH_STATUS_LABELS: Record<DpMatchStatus | 'NO_CANDIDATE', string> = {
  AUTO: 'Auto approved',
  NEEDS_REVIEW: 'Needs review',
  APPROVED: 'Human approved',
  REJECTED: 'Rejected',
  NO_CANDIDATE: 'No candidates',
};
const MATCH_REVIEW_REASON_LABELS: Record<string, string> = {
  candidate_ambiguity: 'The candidate relationship is ambiguous.',
  identity_unknown: 'Evidence for the same product and option is insufficient.',
  identity_incomplete: 'Product identity evidence is incomplete.',
  source_candidate_unresolved: 'No verified competitor candidate is available.',
  variant_evidence_incomplete: 'Competitor option and quantity evidence is incomplete.',
  offer_identity_incomplete: 'Competitor offer identity evidence is incomplete.',
};
const PRICE_STATUS_LABELS: Record<DpPriceStatus, string> = {
  PROPOSED: 'Proposed',
  HELD: 'Held',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PUBLISHED: 'Published',
};
const readError = (error: unknown, fallback: string) => {
  if (!(error instanceof CompetitorV2ApiError)) {
    return fallback;
  }
  if (error.status === 401) {
    return 'Sign in with an authorized account to read this information.';
  }
  if (error.status === 403) {
    return 'You do not have permission to read this information.';
  }
  if (error.status === 408) {
    return 'The read timed out. Retry to load the saved information.';
  }
  return fallback;
};
const matchMoney = (value: string | null, currency: string | null) =>
  value == null ? 'Unknown' : `${value} ${currency ?? 'Currency unknown'}`;
// Our price leads in USD so it compares with competitor USD prices; the source amount stays alongside.
const ourMoney = (usd: string | null | undefined, value: string | null, currency: string | null) =>
  usd == null ? matchMoney(value, currency) : `${usd} USD (${matchMoney(value, currency)})`;
const present = (value: string | null | undefined, missing: string) => value?.trim() || missing;
const totalQuantity = (unitCount: number | null, unit: string | null, packCount: number | null) =>
  unitCount && unit && packCount ? `${unitCount}${unit} × ${packCount} = ${unitCount * packCount}${unit}` : 'Unknown';
const candidateKey = (candidate: Pick<DpMatchCandidate, 'sourceProductId' | 'optionKey' | 'market'>) =>
  `${candidate.sourceProductId}\u0000${candidate.optionKey}\u0000${candidate.market}`;
const MAX_VISIBLE_CANDIDATES = 3;

/** Product thumbnail; a grey box when there is no https image or it fails to load. */
function DpThumb({ src, size, alt }: { src: string | null | undefined; size: number; alt: string }) {
  // Keyed by URL so a failure does not hide the next product image in the same component.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const box = { width: size, height: size, flexShrink: 0 };
  return src?.startsWith('https://') && src !== failedSrc ? (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      // Competitor CDNs never learn the admin URL; harmless for our own images too.
      referrerPolicy="no-referrer"
      width={size}
      height={size}
      className="rounded bg-white border"
      style={{ ...box, objectFit: 'contain' }}
      onError={() => setFailedSrc(src)}
    />
  ) : (
    <div data-testid="dp-thumb-placeholder" aria-hidden="true" className="rounded bg-secondary-subtle" style={box} />
  );
}

function MatchDecision({
  match,
  summary,
  client,
  onSaved,
  onNotice,
}: {
  match: DpMatch;
  summary: Pick<DpReviewSummary, 'targetSku' | 'source'>;
  client: CompetitorV2Client;
  onSaved: () => Promise<void>;
  onNotice: (message: string) => void;
}) {
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  const [targetQuantity, setTargetQuantity] = useState(() => dpQuantityDraft(null));
  const [sourceQuantity, setSourceQuantity] = useState(() => dpQuantityDraft(null));
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const currentKey =
    match.sourceProductId && match.optionKey && match.market
      ? candidateKey({ sourceProductId: match.sourceProductId, optionKey: match.optionKey, market: match.market })
      : null;
  const confirmed = match.status === 'AUTO' || match.status === 'APPROVED';
  const chosen = match.candidates.find((item) => candidateKey(item) === chosenKey);
  // The current match first, then the highest scores (unscored last); staff only need the top few.
  const visibleCandidates = [...match.candidates]
    .sort(
      (a, b) =>
        Number(candidateKey(b) === currentKey) - Number(candidateKey(a) === currentKey) ||
        (b.score ?? Number.NEGATIVE_INFINITY) - (a.score ?? Number.NEGATIVE_INFINITY),
    )
    .slice(0, MAX_VISIBLE_CANDIDATES);
  const targetFacts = match.quantities
    ? {
        unitCount: match.quantities.targetUnitCount,
        unit: match.quantities.targetUnit,
        packCount: match.quantities.targetPackCount,
      }
    : match.target.targetQuantity;
  const targetUrl = safeProductUrl(match.target.productUrl, 'skin-seoul.com');
  const choose = (item: DpMatchCandidate) => {
    const isCurrent = candidateKey(item) === currentKey;
    setChosenKey(candidateKey(item));
    setTargetQuantity(dpQuantityDraft(targetFacts));
    setSourceQuantity(
      dpQuantityDraft(
        isCurrent && match.quantities
          ? {
              unitCount: match.quantities.sourceUnitCount,
              unit: match.quantities.sourceUnit,
              packCount: match.quantities.sourcePackCount,
            }
          : item.observation?.unitCount && item.observation.unit && item.observation.packCount
            ? {
                unitCount: item.observation.unitCount,
                unit: item.observation.unit,
                packCount: item.observation.packCount,
              }
            : null,
      ),
    );
    setNote('');
  };
  const decide = async (decision: 'APPROVED' | 'REJECTED') => {
    if (pending) {
      return;
    }
    const target = dpQuantityInput(targetQuantity);
    const source = dpQuantityInput(sourceQuantity);
    if (decision === 'APPROVED' && (!chosen || !target || !source)) {
      onNotice('Select a candidate and verify both quantities before approval.');
      return;
    }
    setPending(true);
    onNotice('');
    try {
      await client.decideDpMatch(summary.targetSku, summary.source, {
        decision,
        ...(decision === 'APPROVED' && chosen && target && source
          ? {
              sourceProductId: chosen.sourceProductId,
              optionKey: chosen.optionKey,
              market: chosen.market,
              quantities: {
                targetUnitCount: target.unitCount,
                targetUnit: target.unit,
                targetPackCount: target.packCount,
                sourceUnitCount: source.unitCount,
                sourceUnit: source.unit,
                sourcePackCount: source.packCount,
              },
            }
          : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onNotice('Match decision saved.');
      setChosenKey(null);
      setNote('');
    } catch (error) {
      onNotice(
        error instanceof CompetitorV2ApiError && error.status === 400
          ? `The server rejected this decision: ${error.message}`
          : 'Could not save the match decision.',
      );
    } finally {
      setPending(false);
      await onSaved();
    }
  };
  return (
    <Card>
      <Card.Header>
        {summary.targetSku} · {SOURCES[summary.source as keyof typeof SOURCES] ?? summary.source}
      </Card.Header>
      <Card.Body>
        <Badge bg={match.status === 'NEEDS_REVIEW' ? 'warning' : 'secondary'}>
          {MATCH_STATUS_LABELS[match.status] ?? match.status}
        </Badge>
        {match.status === 'NEEDS_REVIEW' && (
          <Alert variant="warning" className="mt-2 mb-0 py-2">
            <strong>Why this match was not confirmed automatically:</strong>{' '}
            {match.reasonCode
              ? (MATCH_REVIEW_REASON_LABELS[match.reasonCode] ?? `Further review needed (${match.reasonCode})`)
              : 'Reason not recorded'}
            <small className="d-block">A candidate score does not confirm a match.</small>
          </Alert>
        )}
        {match.reviewedBy && (
          <small className="d-block mt-2">
            Reviewed by {match.reviewedBy}
            {match.reviewedAt ? ` · ${new Date(match.reviewedAt).toLocaleString('en-GB')}` : ''}
          </small>
        )}
        {match.status !== 'REJECTED' && (
          <div className="mt-3">
            <Button type="button" variant="outline-danger" disabled={pending} onClick={() => void decide('REJECTED')}>
              Reject this competitor match
            </Button>
            <small className="d-block mt-1">
              Reject only this SKU and competitor relationship. Automatic matching keeps this decision.
            </small>
          </div>
        )}
        <section className="border rounded p-3 mt-3 d-flex gap-3" aria-label="SkinSeoul product">
          <DpThumb src={match.target.imageUrl} size={120} alt="SkinSeoul product image" />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h3 className="fs-5">SkinSeoul product · {match.target.brand}</h3>
            <strong className="d-block text-break">{match.target.name}</strong>
            <div>
              List price {ourMoney(match.target.regularPriceUsd, match.target.regularPrice, match.target.currency)} ·
              Selling price {ourMoney(match.target.salePriceUsd, match.target.salePrice, match.target.currency)}
            </div>
            {(match.target.regularPriceUsd != null || match.target.salePriceUsd != null) && (
              <small className="d-block text-muted">
                USD is the store price for US customers and can differ from the DP pricing exchange rate.
              </small>
            )}
            <div>Option {match.target.optionLabel ?? 'Unknown'}</div>
            <div>
              Total quantity{' '}
              {totalQuantity(targetFacts?.unitCount ?? null, targetFacts?.unit ?? null, targetFacts?.packCount ?? null)}
            </div>
            <small className="d-block text-muted">
              Product label: size {match.target.size ?? 'Unknown'} · Packs {match.target.pack ?? 'Unknown'}
            </small>
            {targetUrl && (
              <a href={targetUrl} target="_blank" rel="noopener noreferrer">
                View SkinSeoul product
              </a>
            )}
          </div>
        </section>
        <div className="mt-3" aria-label="Competitor product">
          <h3 className="fs-5">Compare competitor candidates</h3>
          {match.candidates.length === 0 && <p>No saved candidates.</p>}
          {match.candidates.length > MAX_VISIBLE_CANDIDATES && (
            <p className="small text-muted">
              Showing {MAX_VISIBLE_CANDIDATES} of {match.candidates.length} candidates
            </p>
          )}
          {visibleCandidates.map((item, index) => {
            const key = candidateKey(item);
            const observation = item.observation;
            const sourceUrl = safeProductUrl(observation?.sourceUrl ?? null, SOURCE_HOSTS[summary.source]);
            const isSelected = key === chosenKey;
            return (
              <div
                role="group"
                aria-label={`Candidate ${index + 1} comparison`}
                className={`border rounded p-3 mb-3 ${isSelected ? 'border-primary border-2' : ''}`}
                key={key}
              >
                <div className="d-flex gap-3 align-items-start">
                  <DpThumb src={observation?.imageUrl} size={120} alt="Competitor product image" />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="text-muted small">
                      Candidate {index + 1} · {present(observation?.brand, 'Brand unknown')}
                    </div>
                    <div className="d-flex gap-2 align-items-center flex-wrap mb-1">
                      <strong className="fs-5 text-break">
                        {present(observation?.productName, 'Product name unknown')}
                      </strong>
                      <span className="small text-break">Competitor product ID: {item.sourceProductId}</span>
                      <Badge bg="light" text="dark">
                        {item.score === null ? 'Match score unavailable' : `Match score ${item.score}`}
                      </Badge>
                      {confirmed && key === currentKey && <Badge bg="success">Current match</Badge>}
                      {match.status === 'NEEDS_REVIEW' && key === currentKey && (
                        <Badge bg="secondary">Review suggestion</Badge>
                      )}
                      {isSelected && <Badge bg="primary">Selected (not saved)</Badge>}
                    </div>
                    <div className="small text-muted text-break">
                      Option key: {item.optionKey} · Market {item.market}
                    </div>
                    {item.note && <div className="small text-muted">{item.note}</div>}
                    {observation ? (
                      <table
                        aria-label="Product comparison"
                        className="table table-sm table-bordered align-middle my-2"
                      >
                        <tbody>
                          <tr>
                            <th scope="row">Price</th>
                            <td>
                              List {matchMoney(observation.listPrice, observation.currency)} · Sale{' '}
                              {matchMoney(observation.salePrice, observation.currency)}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Availability</th>
                            <td>{observation.availability ?? 'Unknown'}</td>
                          </tr>
                          <tr>
                            <th scope="row">Option</th>
                            <td>{present(observation.optionLabel, 'Unknown option')}</td>
                          </tr>
                          <tr>
                            <th scope="row">Total quantity</th>
                            <td>{totalQuantity(observation.unitCount, observation.unit, observation.packCount)}</td>
                          </tr>
                          <tr>
                            <th scope="row">Observed</th>
                            <td>
                              {observation.observedAt
                                ? new Date(observation.observedAt).toLocaleString('en-GB')
                                : 'Unknown'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <small className="d-block my-2">No current observation for this candidate.</small>
                    )}
                    {sourceUrl && (
                      <a
                        className="btn btn-outline-primary btn-sm"
                        href={sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View competitor product
                      </a>
                    )}
                    {!isSelected && (
                      <Button className="mt-2 ms-2" size="sm" disabled={pending} onClick={() => choose(item)}>
                        Select candidate
                      </Button>
                    )}
                    {isSelected && (
                      <Form
                        aria-label="Verify both quantities"
                        className="mt-3 border-top pt-3"
                        onSubmit={(event: FormEvent) => {
                          event.preventDefault();
                          void decide('APPROVED');
                        }}
                      >
                        <QuantityFields label="SkinSeoul" value={targetQuantity} onChange={setTargetQuantity} />
                        <QuantityFields label="Competitor" value={sourceQuantity} onChange={setSourceQuantity} />
                        <Form.Label className="small mb-0">Review note (optional)</Form.Label>
                        <Form.Control
                          aria-label="Review note"
                          placeholder="Why this match and quantity are right, e.g. Competitor sells 40 ml single; ours is 50 ml x 2 set"
                          maxLength={2000}
                          value={note}
                          onChange={(event) => setNote(event.target.value)}
                        />
                        <div className="d-flex gap-2 mt-2">
                          <Button type="submit" disabled={pending}>
                            Approve this match
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            disabled={pending}
                            onClick={() => setChosenKey(null)}
                          >
                            Clear selection
                          </Button>
                        </div>
                      </Form>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
}

function ReviewDetail({
  summary,
  client,
  autoOnly = false,
}: {
  summary: Pick<DpReviewSummary, 'targetSku' | 'source'>;
  client: CompetitorV2Client;
  /** Shows the card only for an AUTO match. */
  autoOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState('');
  const detail = useQuery({
    queryKey: ['dp-match-detail', summary.targetSku, summary.source],
    queryFn: () => client.getDpMatch(summary.targetSku, summary.source),
    retry: false,
  });
  return (
    <>
      {detail.isPending ? (
        <Spinner animation="border" aria-label="Loading match detail" />
      ) : detail.isError ? (
        <Alert variant="danger">
          {readError(detail.error, 'Could not load match evidence.')}
          <Button
            size="sm"
            variant="outline-secondary"
            className="ms-2"
            disabled={detail.isFetching}
            onClick={() => void detail.refetch()}
          >
            Retry match detail
          </Button>
        </Alert>
      ) : autoOnly && detail.data.status !== 'AUTO' ? (
        <Alert variant="secondary">No auto-approved match for this SKU and competitor.</Alert>
      ) : detail.data.status === 'NO_CANDIDATE' ? (
        <Alert variant="secondary">{MATCH_STATUS_LABELS.NO_CANDIDATE}</Alert>
      ) : (
        <MatchDecision
          key={`${summary.targetSku}:${summary.source}`}
          match={detail.data}
          summary={summary}
          client={client}
          onNotice={setNotice}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ['dp-reviews'] });
            await queryClient.invalidateQueries({ queryKey: ['dp-match-detail', summary.targetSku, summary.source] });
          }}
        />
      )}
      {notice && (
        <Alert role="status" className="mt-2">
          {notice}
        </Alert>
      )}
    </>
  );
}

function DpPriceReviews() {
  const { apiUrl } = getConfig();
  const client = useMemo(() => new CompetitorV2Client(apiUrl), [apiUrl]);
  const [cursor, setCursor] = useState<string>();
  const [back, setBack] = useState<Array<string | undefined>>([]);
  const [status, setStatus] = useState<DpPriceStatus>('PROPOSED');
  const [holdReason, setHoldReason] = useState('');
  const [draftQ, setDraftQ] = useState('');
  const [q, setQ] = useState('');
  const [skuLastDigit, setSkuLastDigit] = useState('');
  const [selected, setSelected] = useState<DpPriceReviewItem>();
  // Set when a stale decision's row left the queue, so the page still says why nothing was decided.
  const [staleClosed, setStaleClosed] = useState(false);
  const reviews = useQuery({
    queryKey: ['dp-price-reviews', status, holdReason, q, skuLastDigit, cursor],
    queryFn: () => client.listDpPriceReviews(status, holdReason, cursor, q, skuLastDigit),
    retry: false,
  });
  const reset = () => {
    setCursor(undefined);
    setBack([]);
    setSelected(undefined);
  };
  return (
    <Row>
      <Col xs={12} className="mb-3 d-flex gap-2 flex-wrap">
        <Form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            setQ(draftQ.trim());
            reset();
          }}
          className="d-flex gap-2"
        >
          <Form.Control
            aria-label="Product search"
            placeholder="Product name · brand · SKU"
            maxLength={128}
            value={draftQ}
            onChange={(event) => setDraftQ(event.target.value)}
          />
          <Button type="submit" className="text-nowrap">
            Search
          </Button>
        </Form>
        <Form.Select
          aria-label="Price status"
          className="w-auto"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as DpPriceStatus);
            reset();
          }}
        >
          {/* Held rows can still take an entered price; Published rows are already on the store, so the queue leaves them out. */}
          {Object.entries(PRICE_STATUS_LABELS)
            .filter(([value]) => value !== 'PUBLISHED')
            .map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
        </Form.Select>
        <Form.Select
          aria-label="Review reason"
          className="w-auto"
          value={holdReason}
          onChange={(event) => {
            setHoldReason(event.target.value);
            reset();
          }}
        >
          <option value="">All</option>
          {/* Batch's review reasons, and the publish errors auto publish leaves on a PROPOSED row. */}
          {[
            'large_change_review',
            'top_seller_increase_review',
            'official_store_increase_review',
            'restore_cost_review',
            // A stale refusal is never stored on a row.
            ...Object.keys(DP_PRICE_ERRORS).filter((code) => code !== 'DP_PRICE_REVIEW_STALE'),
          ].map((code) => (
            <option value={code} key={code}>
              {holdReasonLabel(code)}
            </option>
          ))}
        </Form.Select>
        <Form.Group controlId="dp-price-sku-last-digit" className="d-flex gap-2 align-items-center">
          <Form.Label className="mb-0 text-nowrap small">SKU Ends With</Form.Label>
          <Form.Select
            className="w-auto"
            value={skuLastDigit}
            title="Split the queue between reviewers by the last digit of the SKU"
            onChange={(event) => {
              setSkuLastDigit(event.target.value);
              reset();
            }}
          >
            <option value="">All</option>
            {'0123456789'.split('').map((digit) => (
              <option value={digit} key={digit}>
                {digit}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col xs={12} md={4}>
        <Card>
          <Card.Header>Price review queue</Card.Header>
          <Card.Body>
            {reviews.isPending ? (
              <Spinner animation="border" aria-label="Loading price reviews" />
            ) : reviews.isError ? (
              <Alert variant="danger">
                {readError(reviews.error, 'Could not load the price review queue.')}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="ms-2"
                  disabled={reviews.isFetching}
                  onClick={() => void reviews.refetch()}
                >
                  Retry price queue
                </Button>
              </Alert>
            ) : reviews.data.items.length === 0 ? (
              <p>No price reviews.</p>
            ) : (
              reviews.data.items.map((item) => (
                <Button
                  key={item.targetSku}
                  variant={selected?.targetSku === item.targetSku ? 'primary' : 'light'}
                  className="w-100 text-start mb-2 d-flex gap-2 align-items-center"
                  onClick={() => {
                    setSelected(item);
                    setStaleClosed(false);
                  }}
                >
                  <DpThumb src={item.imageUrl} size={56} alt="" />
                  <span style={{ minWidth: 0 }}>
                    <strong className="d-block">{item.targetSku}</strong>
                    {dpCurrentPriceText(item)} →{' '}
                    {dpProposedPrice(item) ? `${dpProposedPrice(item)} SGD` : 'No new price'} ·{' '}
                    {PRICE_STATUS_LABELS[item.status] ?? item.status}
                    {!item.proposedSalePrice &&
                      Number(item.proposedRegularPrice) > Number(item.previousEffectivePrice) && (
                        <span className="d-block small">
                          Regular price rises to {matchMoney(item.proposedRegularPrice, 'SGD')}; the sale price is
                          removed
                        </span>
                      )}
                    <small className="d-block">{dpQueueContextText(item)}</small>
                    {item.holdReason && <small className="d-block">{holdReasonLabel(item.holdReason)}</small>}
                  </span>
                </Button>
              ))
            )}
            <div className="d-flex justify-content-between">
              <Button
                size="sm"
                disabled={!back.length || reviews.isFetching}
                onClick={() => {
                  setCursor(back[back.length - 1]);
                  setBack(back.slice(0, -1));
                  setSelected(undefined);
                }}
              >
                Previous
              </Button>
              <Button
                size="sm"
                disabled={!reviews.data?.nextCursor || reviews.isFetching}
                onClick={() => {
                  setBack([...back, cursor]);
                  setCursor(reviews.data!.nextCursor!);
                  setSelected(undefined);
                }}
              >
                Next
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} md={8}>
        {selected ? (
          <DpStaffPriceReview
            key={selected.targetSku}
            row={selected}
            // The decision answers with the bare stored row; keep the links and current price already shown.
            onDone={(result) => setSelected({ ...selected, ...result, competitorSource: selected.competitorSource })}
            // Show the refetched row (or none when it left the queue) so the next decision sends its new version.
            onStale={() =>
              void reviews.refetch().then(({ data }) => {
                const fresh = data?.items.find((item) => item.targetSku === selected.targetSku);
                setStaleClosed(!fresh);
                setSelected((current) => (current?.targetSku === selected.targetSku ? fresh : current));
              })
            }
          />
        ) : (
          <>
            {staleClosed && <Alert variant="danger">{DP_PRICE_ERRORS['DP_PRICE_REVIEW_STALE']}</Alert>}
            <p>Select a SKU to review its price.</p>
          </>
        )}
      </Col>
    </Row>
  );
}

function DpLeanReviews() {
  const { apiUrl } = getConfig();
  const client = useMemo(() => new CompetitorV2Client(apiUrl), [apiUrl]);
  const [source, setSource] = useState('');
  const [status, setStatus] = useState<DpMatchStatus | ''>('NEEDS_REVIEW');
  const [draftQ, setDraftQ] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string>();
  const [back, setBack] = useState<Array<string | undefined>>([]);
  const [selected, setSelected] = useState<DpReviewSummary>();
  const reviews = useQuery({
    queryKey: ['dp-reviews', source, status, q, cursor],
    queryFn: () => client.listDpReviews(source, status, cursor, q),
    retry: false,
  });
  const reset = () => {
    setCursor(undefined);
    setBack([]);
    setSelected(undefined);
  };
  return (
    <Container fluid className="py-3">
      <h2>Competitor matching · pricing</h2>
      <p>Candidate and quantity review does not authorize price publication.</p>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            setQ(draftQ.trim());
            reset();
          }}
          className="d-flex gap-2"
        >
          <Form.Control
            aria-label="Product search"
            placeholder="Product name · brand · SKU"
            maxLength={256}
            value={draftQ}
            onChange={(event) => setDraftQ(event.target.value)}
          />
          <Button type="submit" className="text-nowrap">
            Search
          </Button>
        </Form>
        <Form.Select
          aria-label="Competitor"
          className="w-auto"
          value={source}
          onChange={(event) => {
            setSource(event.target.value);
            reset();
          }}
        >
          <option value="">All competitors</option>
          {Object.entries(SOURCES).map(([value, name]) => (
            <option value={value} key={value}>
              {name}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          aria-label="Match status"
          className="w-auto"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as typeof status);
            reset();
          }}
        >
          <option value="NEEDS_REVIEW">Needs review</option>
          <option value="AUTO">Auto approved</option>
          <option value="APPROVED">Human approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="">All</option>
        </Form.Select>
      </div>
      <Row>
        <Col xs={12} lg={3}>
          <Card>
            <Card.Header>Match review queue</Card.Header>
            <Card.Body>
              {reviews.isPending ? (
                <Spinner animation="border" aria-label="Loading reviews" />
              ) : reviews.isError ? (
                <Alert variant="danger">
                  {readError(reviews.error, 'Could not load match reviews.')}
                  <Button
                    size="sm"
                    className="ms-2"
                    disabled={reviews.isFetching}
                    onClick={() => void reviews.refetch()}
                  >
                    Retry match reviews
                  </Button>
                </Alert>
              ) : reviews.data.items.length === 0 ? (
                <p>No matching reviews.</p>
              ) : (
                reviews.data.items.map((item) => (
                  <Button
                    key={`${item.targetSku}:${item.source}`}
                    variant={
                      selected?.targetSku === item.targetSku && selected.source === item.source ? 'primary' : 'light'
                    }
                    className="w-100 text-start mb-2 d-flex gap-2 align-items-center"
                    onClick={() => setSelected(item)}
                  >
                    <DpThumb src={item.imageUrl} size={56} alt="" />
                    <span style={{ minWidth: 0 }}>
                      <strong className="d-block">{item.productName ?? 'Product name unknown'}</strong>
                      <small className="d-block">{item.brand ?? 'Brand unknown'}</small>
                      {item.targetSku} · {SOURCES[item.source as keyof typeof SOURCES] ?? item.source} ·{' '}
                      {MATCH_STATUS_LABELS[item.status] ?? item.status}
                    </span>
                  </Button>
                ))
              )}
              <div className="d-flex justify-content-between">
                <Button
                  size="sm"
                  disabled={!back.length || reviews.isFetching}
                  onClick={() => {
                    setCursor(back[back.length - 1]);
                    setBack(back.slice(0, -1));
                    setSelected(undefined);
                  }}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  disabled={!reviews.data?.nextCursor || reviews.isFetching}
                  onClick={() => {
                    setBack([...back, cursor]);
                    setCursor(reviews.data!.nextCursor!);
                    setSelected(undefined);
                  }}
                >
                  Next
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={9}>
          {selected ? (
            <ReviewDetail key={`${selected.targetSku}:${selected.source}`} summary={selected} client={client} />
          ) : (
            <p>Select a SKU to review.</p>
          )}
        </Col>
      </Row>
    </Container>
  );
}

function DpLeanContent() {
  const [view, setView] = useState<'matches' | 'prices'>('matches');
  return (
    <>
      <div className="d-flex gap-2 px-3 pt-3">
        <Button variant={view === 'matches' ? 'primary' : 'outline-primary'} onClick={() => setView('matches')}>
          Match review
        </Button>
        <Button variant={view === 'prices' ? 'primary' : 'outline-primary'} onClick={() => setView('prices')}>
          Price review
        </Button>
      </div>
      {view === 'matches' ? (
        <DpLeanReviews />
      ) : (
        <Container fluid className="py-3">
          <h2>Price review</h2>
          <p>Approve to publish the proposed price (or an entered price) to the store immediately, or reject it.</p>
          <DpPriceReviews />
        </Container>
      )}
    </>
  );
}

/** The match detail card alone for the competitor code and SKU in the path, e.g. /competitor/matching/ys/100000080. */
function DpMatchDetail() {
  const { code = '', sku = '' } = useParams();
  const { apiUrl } = getConfig();
  const client = useMemo(() => new CompetitorV2Client(apiUrl), [apiUrl]);
  const source = SOURCE_CODES.get(code);
  return (
    <Container fluid className="py-3">
      {source ? (
        <ReviewDetail key={`${sku}:${source}`} summary={{ targetSku: sku, source }} client={client} autoOnly />
      ) : (
        <Alert variant="secondary">Unknown competitor code: {code}</Alert>
      )}
    </Container>
  );
}

/** Mounts the staff session so every DP read and decision carries the staff token. */
function StaffSession({ children }: { children: ReactNode }) {
  const staff = useStaffSession();
  return staff.status === 'loading' ? (
    <Spinner animation="border" aria-label="Checking staff access" />
  ) : (
    <>{children}</>
  );
}

// The Logto hooks throw outside a mounted provider, which only exists when an app id is configured.
const withStaffSession = (page: ReactNode) => (
  <>{getConfig().logtoAppId ? <StaffSession>{page}</StaffSession> : page}</>
);

export default function DpLeanPage() {
  return withStaffSession(<DpLeanContent />);
}

export function DpMatchDetailPage() {
  return withStaffSession(<DpMatchDetail />);
}
